'use client';

import { Suspense, useEffect, useRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { apiFetch } from '@/lib/api';
import { delocalizePath } from '@/lib/i18n-routes';
import type { LandingInput } from '@/lib/traffic-source';
import { useSession } from '@/hooks/use-session';

/** Track whether a real user interaction has occurred this session. */
export const interactionState = { happened: false };
// Captured once per full load, including when the first view waits for interaction.
let landingSent = false;
const initialLanding: LandingInput | undefined =
  typeof window === 'undefined'
    ? undefined
    : (() => {
        const params = new URLSearchParams(window.location.search);
        return {
          referrer: document.referrer.slice(0, 500),
          utmSource: params.get('utm_source')?.slice(0, 500),
          utmMedium: params.get('utm_medium')?.slice(0, 500),
          utmCampaign: params.get('utm_campaign')?.slice(0, 500),
          clickId: (['gclid', 'msclkid', 'fbclid'] as const).find((id) =>
            params.has(id),
          ),
        };
      })();
const SEARCH_VIEW_PARAMS = new Set(['sort', 'limit', 'offset', 'preview']);
if (typeof window !== 'undefined') {
  const markInteraction = () => {
    interactionState.happened = true;
  };
  window.addEventListener('mousemove', markInteraction, {
    once: true,
    passive: true,
  });
  window.addEventListener('scroll', markInteraction, {
    once: true,
    passive: true,
  });
  window.addEventListener('touchstart', markInteraction, {
    once: true,
    passive: true,
  });
  window.addEventListener('keydown', markInteraction, {
    once: true,
    passive: true,
  });
  window.addEventListener('click', markInteraction, {
    once: true,
    passive: true,
  });
}

/**
 * Inner component that does the actual logging.
 * Separated so we can wrap it in Suspense (useSearchParams requirement).
 */
function PageViewLoggerInner() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { status } = useSession();
  const lastLogged = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const viewParams = new URLSearchParams(searchParams?.toString());
  viewParams.delete('preview');
  const queryString = viewParams.toString();

  useEffect(() => {
    if (status === 'loading') return;

    // Skip obvious bots (headless browsers, webdriver)
    if (typeof navigator !== 'undefined' && navigator.webdriver) return;

    // Filtered searches carry their full URL in search_query instead.
    if (
      delocalizePath(pathname).pathname === '/search' &&
      Array.from(new URLSearchParams(queryString).keys()).some(
        (param) => !SEARCH_VIEW_PARAMS.has(param),
      )
    )
      return;

    const url = pathname + (queryString ? `?${queryString}` : '');

    // Include status so a transition (e.g. guest → authenticated after
    // terms acceptance) re-logs the same URL with the real user identity.
    const key = `${status}:${url}`;
    if (key === lastLogged.current) return;

    // Clear any pending timer from a previous rapid navigation
    if (timerRef.current) clearTimeout(timerRef.current);

    const isGuest = status !== 'authenticated';
    let interval: ReturnType<typeof setInterval> | null = null;

    function doLog() {
      lastLogged.current = key;
      const segments = pathname.split('/').filter(Boolean);
      const entityType = segments[0] || 'home';

      const landing = landingSent ? undefined : initialLanding;
      landingSent = true;
      apiFetch('/api/log/page-view', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event: 'page_view',
          entityType,
          description: `Viewed ${pathname}`,
          url,
          landing,
        }),
        keepalive: true,
      }).catch(() => {});
    }

    if (isGuest) {
      // For guests: wait 3s AND require a real interaction (mouse, scroll, touch, key).
      // Crawlers render the page but don't produce user interactions.
      timerRef.current = setTimeout(() => {
        if (interactionState.happened) {
          doLog();
        }
        // If no interaction after 3s, keep checking every 2s up to 15s total
        else {
          let checks = 0;
          interval = setInterval(() => {
            checks++;
            if (interactionState.happened) {
              clearInterval(interval!);
              doLog();
            } else if (checks >= 6) {
              clearInterval(interval!);
              // No interaction after 15s — almost certainly a bot, don't log
            }
          }, 2000);
        }
      }, 3000);
    } else {
      doLog();
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (interval) clearInterval(interval);
    };
  }, [pathname, queryString, status]);

  return null;
}

/**
 * Global page view logger. Mount once at layout level.
 * Automatically logs every route change to /api/log/page-view
 * for both authenticated and guest users.
 */
export function PageViewLogger() {
  return (
    <Suspense fallback={null}>
      <PageViewLoggerInner />
    </Suspense>
  );
}
